/**
 * Where a browser's live socket goes, tenant and all.
 *
 * A browser `WebSocket` sends no headers of its own choosing, so the phone's
 * `x-alluzo-tenant` cannot ride on the upgrade. The server takes the tenant
 * from `?tenant=<uuid>` instead (`adoptQueryTenant` in
 * apps/server/src/tenant-context.ts copies it into the header before the
 * identity is read, so it meets the same membership check). The session
 * cookie travels by itself: the socket is on the page's own origin.
 *
 * The app names itself the same way, as `?client=` beside the tenant, for the
 * `x-alluzo-client` header the phone's sockets send: without it OS would serve
 * a live call as an old client (web-redesign-02 phase 1).
 *
 * The setup seat rides the query the same way (`?seat=setup`): the server picks the seat before it reads the first frame,
 * so a setup call (no tenant) is refused or served by the setup's rules.
 *
 * The paths are the phone's own (`assistantLiveWsUrl`, `dictationWsUrl`); this
 * only adds the seat, the tenant and the client. Pure, so `live-urls.test.ts`
 * proves it.
 */
import type { LiveSeat } from '@alluzo/shared';

/** `url` with `?seat=` (or `&seat=`) added for a setup seat; unchanged for the assistant's. */
export const withSeat = (url: string, seat: LiveSeat | undefined): string => {
  if (seat === undefined || seat === 'assistant') return url;
  const joiner = url.includes('?') ? '&' : '?';
  return `${url}${joiner}seat=${encodeURIComponent(seat)}`;
};

/** `url` with `?tenant=` (or `&tenant=`) added; unchanged when there is no tenant. */
export const withTenant = (url: string, tenantId: string | null | undefined): string => {
  if (tenantId === null || tenantId === undefined || tenantId === '') return url;
  const joiner = url.includes('?') ? '&' : '?';
  return `${url}${joiner}tenant=${encodeURIComponent(tenantId)}`;
};

/** `url` with `?client=` (or `&client=`) added: the `x-alluzo-client` value a browser socket cannot send as a header. */
export const withClient = (url: string, client: string): string => {
  const joiner = url.includes('?') ? '&' : '?';
  return `${url}${joiner}client=${encodeURIComponent(client)}`;
};
